import * as React from 'react';
import { CmdSimpleSelectFilter } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress from '../../../hooks/useKeyPress';
import {
  ICheckedTableItems,
  IFilterChipsValues,
  IFilterTable,
  IFilterTripsOverview,
  ITripsOverviewColumn,
  ITripsOverviewTableData,
} from '../interfaces';
import { getIsColumnLast } from '../TableHeader/TableHeader';
import { ESCAPE_KEY } from '../constants';
import {
  checkedTableItemsAtom,
  columnsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface ISimpleSelectFilterProps {
  id: string;
  filterValues: IFilterTable;
  search?: boolean;
  title?: string;
}

function SimpleSelectFilter(props: ISimpleSelectFilterProps): JSX.Element {
  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );
  const columnsTable = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const setFilterForRequest = useSetRecoilState<IFilterTripsOverview>(filterForRequestAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const { formatMessage: f } = useIntl();
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const { trackEvent } = useAnalytics();

  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();

  useOutsideClick(clickOutsideRef, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  useKeyPress(ESCAPE_KEY, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  const handleOnClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, value: string) => {
    event.preventDefault();
    const item = props.filterValues.values?.find((i) => String(i.value) === String(value));

    if (item) {
      trackEvent(['ride book', 'click', `Ride book open simple select filter: ${props.id}`]);
      const filterValues: IFilterChipsValues = {
        ...item,
        title: props.title ? props.title : '',
        type: 'simpleSelect',
      };

      const data = {};
      data[props.id] = item.value;

      setFilterForRequest((prevFilter) => ({
        ...prevFilter,
        ...data,
      }));
      setOffset(0);
      setTableData(null);
      setRideItemTimestamps([]);
      setSummaryItemIndexes([]);
      setRideItemIds([]);
      setHasMore(true);
      setCheckedTableItems({
        mainCheckbox: false,
        intervals: [],
        intervalsBlacklist: [],
        rideIdBlacklist: {},
        rideIds: {},
        customerId: null,
        counts: {},
      });

      setTableActionsFilters(
        (tableActionFilter) =>
          tableActionFilter && {
            ...tableActionFilter,
            id: props.id,
            values: { ...filterValues },
            isOpen: false,
            isActionChipsOpen: false,
            isFilterActive: true,
          }
      );
    }
  };

  const getOptions = (options) => {
    const newOptions =
      options && props.id === 'customerId'
        ? options.map((i) => {
            const item = {
              label: i.item,
              item: i.label,
              value: i.value,
            };
            return item;
          })
        : options;
    return newOptions;
  };

  return (
    <div
      ref={clickOutsideRef}
      style={{
        position: 'absolute',
        left: getIsColumnLast(columnsTable, props.id) ? '-24px' : '-199px',
        bottom: '-15px',
        zIndex: 999,
      }}
      className="table-filter"
    >
      <div className="label open" style={{ textAlign: 'left', minWidth: '220px' }}>
        <div>
          {props.filterValues?.values && (
            <CmdSimpleSelectFilter
              onChangeCallback={handleOnClick}
              options={getOptions(props.filterValues.values)}
              value={
                tableActionsFilters && tableActionsFilters.values
                  ? String(tableActionsFilters.values.value)
                  : ''
              }
              width="285px"
              column={props.title}
              id={props.id}
              name={props.id}
              search={props.search}
              searchPlaceholder={f({ id: 'form.search' })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SimpleSelectFilter;
